import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import heartIconBordered from '../../assets/icons/heart-icon-bordered.svg';
import redHeartIcon from '../../assets/icons/red-heart-icon-filled.svg';
import {
  addWishlistId,
  removeWishlistId,
} from '../../store/reducers/wishlist-reducer';
import { setIsLoginModalOpen } from '../../store/reducers/global-reducer';
import {
  isProductInWishlist,
  normalizeWishlistProductId,
} from '../../helpers/wishlist-utils';

const WishlistHeart = ({ productId }) => {
  const dispatch = useDispatch();
  const authInfo = useSelector((state) => state.auth.authInfo);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const inWishList = useSelector((state) =>
    isProductInWishlist(state.wishlist.selectedWishItems, productId)
  );
  const [pending, setPending] = useState(false);

  const normalizedProductId = normalizeWishlistProductId(productId);

  const addToWishlist = useCallback(async () => {
    if (!isLoggedIn) {
      dispatch(setIsLoginModalOpen({ isLoginModalOpen: true }));
      return;
    }

    if (pending || inWishList) return;

    dispatch(addWishlistId(normalizedProductId));
    setPending(true);

    try {
      const response = await axios.post(
        'user/wishlist/',
        {
          user_id: authInfo.id,
          product_id: normalizedProductId,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json;charset=UTF-8',
            Authorization: `Bearer ${authInfo.token}`,
          },
        }
      );

      if (response.data.status) {
        const savedId = normalizeWishlistProductId(response.data.data?.productId);
        if (savedId && savedId !== normalizedProductId) {
          dispatch(removeWishlistId(normalizedProductId));
          dispatch(addWishlistId(savedId));
        }
        toast.dismiss();
        toast.success(response.data.message);
      } else {
        dispatch(removeWishlistId(normalizedProductId));
        toast.error(response.data.message || 'Could not add to wishlist');
      }
    } catch (error) {
      dispatch(removeWishlistId(normalizedProductId));
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      }
    } finally {
      setPending(false);
    }
  }, [
    authInfo.id,
    authInfo.token,
    dispatch,
    inWishList,
    isLoggedIn,
    normalizedProductId,
    pending,
  ]);

  const removeFromWishlist = useCallback(async () => {
    if (!isLoggedIn || pending || !inWishList) return;

    dispatch(removeWishlistId(normalizedProductId));
    setPending(true);

    try {
      const response = await axios.post(
        'user/remove-from-wishlist/',
        {
          user_id: authInfo.id,
          product_id: normalizedProductId,
        },
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json;charset=UTF-8',
            Authorization: `Bearer ${authInfo.token}`,
          },
        }
      );

      if (response.data.status) {
        toast.success(response.data.message);
      } else {
        dispatch(addWishlistId(normalizedProductId));
        toast.error(response.data.message || 'Could not remove from wishlist');
      }
    } catch (error) {
      dispatch(addWishlistId(normalizedProductId));
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      }
    } finally {
      setPending(false);
    }
  }, [
    authInfo.id,
    authInfo.token,
    dispatch,
    inWishList,
    isLoggedIn,
    normalizedProductId,
    pending,
  ]);

  const handleClick = () => {
    if (inWishList) {
      removeFromWishlist();
    } else {
      addToWishlist();
    }
  };

  return (
    <span
      onClick={handleClick}
      style={{ cursor: pending ? 'wait' : 'pointer', opacity: pending ? 0.7 : 1 }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <img
        src={inWishList ? redHeartIcon : heartIconBordered}
        alt="heart-icon"
        loading="lazy"
        decoding="async"
        title={`${inWishList ? 'Remove' : 'Add'} to wishlist`}
      />
    </span>
  );
};

export default React.memo(WishlistHeart);
